/* eslint-disable react-refresh/only-export-components -- Radix primitive re-exports; leaf design-system file, no stateful component to fast-refresh */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import React from "react";
import { cn } from "@kkp/app-kit";

/**
 * On-brand Dialog primitive built on Radix Dialog.
 *
 * Usage:
 * <Dialog>
 *   <DialogTrigger asChild><Button>Open</Button></DialogTrigger>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Title</DialogTitle>
 *       <DialogDescription>Description text</DialogDescription>
 *     </DialogHeader>
 *     ...
 *     <DialogFooter>
 *       <DialogClose asChild><Button variant="secondary">Cancel</Button></DialogClose>
 *       <Button variant="primary">Save</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 */

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export function DialogPortal({ children }: { children: React.ReactNode }) {
  return <DialogPrimitive.Portal>{children}</DialogPrimitive.Portal>;
}

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-50",
        "bg-[color:rgba(12,9,20,0.50)]",
        "backdrop-blur-[10px]",
        "data-[state=open]:animate-in data-[state=open]:fade-in",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out",
        className,
      )}
      {...props}
    />
  );
});

export type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  wide?: boolean;
};

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(function DialogContent({ className, children, wide, ...props }, ref) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-[560px] -translate-x-1/2 -translate-y-1/2",
          wide && "max-w-[720px]",
          "rounded-[var(--kkp-radius-lg)]",
          "border border-[color:rgba(255,255,255,0.12)]",
          "bg-[color:rgba(24,19,36,0.76)] backdrop-blur-xl",
          "shadow-[0_28px_70px_rgba(0,0,0,0.40),_0_1px_0_rgba(255,255,255,0.10)]",
          "p-6 sm:p-7",
          "outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--kkp-magenta)]/45",
          "data-[state=open]:animate-in data-[state=open]:fade-in-90 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-90 data-[state=closed]:zoom-out-95",
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

export const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("grid gap-1.5 pb-3", className)} {...props} />
);

export const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2",
      className,
    )}
    {...props}
  />
);

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        "text-base font-bold text-[color:rgba(255,255,255,0.92)]",
        className,
      )}
      {...props}
    />
  );
});

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn(
        "text-sm leading-snug text-[color:var(--kkp-text-muted)]",
        className,
      )}
      {...props}
    />
  );
});
